import moment from 'moment';
import React, { CSSProperties as css, FC, useCallback, useState } from 'react';
import DayPicker, { Modifier } from 'react-day-picker';
import 'react-day-picker/lib/style.css';

import classNames from 'classnames';
import { BaseComponent } from '../../../shared/BaseComponent';
import { isMobile, range } from '../../../shared/helpers';
import { Actions as ReportActions } from '../../../stores/reducers/report-reducers';
import DayPickerNavbar from '../../elements/DayPickerNavbar';
import DayPickerWeekday from "../../elements/DayPickerWeekday";
import RangeDayPicker from '../../elements/RangeDayPicker';
import "./DateChoosers.scss";
import { DateChangeType } from './DateFilterTabs/DateFilterTabs';

interface iDaysChooserProps {
    dates: { startDate: moment.Moment, endDate: moment.Moment }[],
    closeable?: boolean,
    calBottom?: boolean,
    style?: css,
    flat?: boolean,
    remove: (date: moment.Moment) => any,
    dispatch: any,
    startDateChange?: any,
    endDateChange?: any,
    onChange?: (datePassed: moment.Moment, dateType?: DateChangeType) => void,
    timeOptionsClassName?: string,
    type?: DateChangeType,
    withTimeOptions?: boolean,
    singleTimeOption?: boolean,
    disabledDays?: Modifier | Modifier[];
}


export const DaysChooser: React.FC<iDaysChooserProps> = ({
  dates = [],
  remove,
  dispatch,
  onChange,
  startDateChange,
  endDateChange,
  type,
  flat = false,
  calBottom = false,
  closeable = false,
  timeOptionsClassName,
  withTimeOptions = true,
  singleTimeOption = false,
  disabledDays
}) => {

  const getFirstOrDefault = useCallback((key, defaultMoment) => {
    if (dates[0] && dates[0][key]) {
      defaultMoment = dates[0][key];
    }
    return defaultMoment;
  }, [dates]);

  const [state, setState] = useState(() => ({
    open: false,
    startTime: {
      hour: getFirstOrDefault('startDate', moment().startOf('day')).format('h'),
      minute: getFirstOrDefault('startDate', moment().startOf('day')).minute(),
      ampm: getFirstOrDefault('startDate', moment().startOf('day')).format('a'),
    },
    endTime: {
      hour: getFirstOrDefault('endDate', moment().endOf('day')).format('h'),
      minute: getFirstOrDefault('endDate', moment().endOf('day')).minute(),
      ampm: getFirstOrDefault('endDate', moment().endOf('day')).format('a'),
    },
    pickedDate: null,
  }));

  const handleDayChange = useCallback(async (date) => {
    setState(prev => ({ ...prev, pickedDate: moment(date) }));

    const { startTime, endTime } = state;

    const startTimeParsed = moment(`${startTime.hour}:${startTime.minute} ${startTime.ampm}`, ["hh:mm A"]);
    const endTimeParsed = moment(`${endTime.hour}:${endTime.minute} ${endTime.ampm}`, ["hh:mm A"]);

    const startDate = moment(date).hour(startTimeParsed.hours()).minute(state.startTime.minute);
    const endDate = moment(date).hour(endTimeParsed.hours()).minute(state.endTime.minute);
    const isAlreadyActive = dates.find(d => d.startDate?.format('YYYYMMDD') === startDate.format('YYYYMMDD'));

    if (isAlreadyActive) {
      remove(startDate);
    } else {
      if (onChange) {
        onChange(type === DateChangeType.start ? startDate : endDate, type);
      } else {
        startDateChange && startDateChange(startDate);
        endDateChange && endDateChange(endDate);
      }
    }
  }, [state, dates, remove, onChange, startDateChange, endDateChange, type]);

  const handleTimeChange = useCallback(async (date) => {
    setState(prev => ({ ...prev, pickedDate: moment(date) }));

    const { startTime, endTime } = state;

    const startTimeParsed = moment(`${startTime.hour}:${startTime.minute} ${startTime.ampm}`, ["hh:mm A"]);
    const endTimeParsed = moment(`${endTime.hour}:${endTime.minute} ${endTime.ampm}`, ["hh:mm A"]);

    const startDate = moment(date).hour(startTimeParsed.hours()).minute(state.startTime.minute);
    const endDate = moment(date).hour(endTimeParsed.hours()).minute(state.endTime.minute);

    const isAlreadyActive = dates.find(d => d.startDate.format('YYYYMMDD') === startDate.format('YYYYMMDD'));

    if (isAlreadyActive) {
      remove(startDate);
    } else {
      if (onChange) {
        onChange(startDate);
      } else {
        startDateChange && startDateChange(startDate);
        endDateChange && endDateChange(endDate);
      }
    }
    dispatch && dispatch(ReportActions.RESET_DATES());
  }, [state, dates, remove, onChange, startDateChange, endDateChange, dispatch]);

  const handleStartTimeChange = useCallback(({ hours, minutes, ampm }) => {
    const { pickedDate } = state;

    setState(prev => ({
      ...prev,
      startTime: { hour: hours, minute: minutes, ampm }
    }));

    const time = moment(`${hours}:${minutes} ${ampm}`, [`hh:mm A`]);
    const startDate = moment(pickedDate || dates[0]?.startDate).hour(time.hours()).minute(minutes);

    if (onChange) {
      onChange(startDate, type);
    } else if (startDateChange) {
      startDateChange(startDate);
    }
  }, [state, onChange, startDateChange, dates, type]);

  // End time change handler
  const handleEndTimeChange = useCallback(({ hours, minutes, ampm }) => {
    const { pickedDate } = state;

    setState(prev => ({
      ...prev,
      endTime: { hour: hours, minute: minutes, ampm }
    }));

    const time = moment(`${hours}:${minutes} ${ampm}`, [`hh:mm A`]);
    const endDate = moment(pickedDate || dates[0]?.endDate).hour(time.hours()).minute(minutes);

    if (onChange) {
      onChange(endDate, type);
    } else if (endDateChange) {
      endDateChange(endDate);
    }
  }, [state, onChange, endDateChange, dates, type]);

  const { startTime, endTime, open } = state;

  const initialDate = type === DateChangeType.start ?
    dates[0]?.startDate?.toDate() :
    type === DateChangeType.end ? dates[0]?.endDate?.toDate() : new Date();

  const rawDate = dates[0]?.[type === DateChangeType.start ? 'startDate' : 'endDate'];
  const date = rawDate ? moment(rawDate) : null;

  const getStartEndTime = (dateTypeParam, startValue, endValue) => {
    if (dateTypeParam === DateChangeType.start) return startValue;
    else return endValue;
  };

  const startTimeOption = {
    hours: getStartEndTime(type, startTime.hour, endTime.hour) ?? date?.format('h'),
    minutes: getStartEndTime(type, startTime.minute, endTime.minute) ?? date?.format('mm'),
    ampm: getStartEndTime(type, startTime.ampm, endTime.ampm) ?? date?.format('A'),
  };

  const selectedDay = moment(state.pickedDate || dates[0]?.[type === DateChangeType.start ? 'startDate' : 'endDate'], 'YYYY-MM-DD').toDate();

  return (
    <div className="date-choosers">
      <div style={{ display: 'flex', flexDirection: flat && !isMobile ? 'row' : 'column' }}>
        <DayPicker
          // @ts-ignore
          style={{ order: calBottom ? 2 : 1 }}
          className="day-picker"
          initialMonth={initialDate}
          onDayClick={handleDayChange}
          selectedDays={selectedDay}
          navbarElement={DayPickerNavbar}
          weekdayElement={DayPickerWeekday}
          captionElement={() => null}
          disabledDays={disabledDays}
          showOutsideDays
        />

        {withTimeOptions && !singleTimeOption && (
          <div className={classNames(timeOptionsClassName, "time-options-container")}>
            <div className='time-option'>
              <strong className="time-options-header">Start Date:</strong>
              <TimeOptions
                hours={startTime.hour}
                minutes={startTime.minute}
                ampm={startTime.ampm}
                onTimeChange={handleStartTimeChange}
              />
            </div>
            <div className='time-option'>
              <strong className="time-options-header">End Date:</strong>
              <TimeOptions
                hours={endTime.hour}
                minutes={endTime.minute}
                ampm={endTime.ampm}
                onTimeChange={handleEndTimeChange}
              />
            </div>
          </div>
        )}

        {singleTimeOption && (
          <div
            className={classNames(timeOptionsClassName, "time-options-container")}
            style={{ justifyContent: 'flex-start' }}
          >
            <div className='time-option'>
              <strong className="time-options-header">Time:</strong>
              <TimeOptions
                hours={+startTimeOption.hours}
                minutes={+startTimeOption.minutes}
                ampm={startTimeOption.ampm.toLowerCase()}
                onTimeChange={type === DateChangeType.start ? handleStartTimeChange : handleEndTimeChange}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};


interface iProps {
    initialStart:moment.Moment,
    initialEnd: moment.Moment,
    calendarHideable: boolean;
    calendarTop: boolean;
    style?: css,
    startDateChange: (date: moment.Moment) => any;
    endDateChange: (date: moment.Moment) => any;
    timeOptionsClassName?: string;
}

export class RangeDateChooser extends BaseComponent<iProps, any> {
    // __changer = isStart => newDate => {
    //     const { startDateChange, endDateChange } = this.props;

    //     isStart ? startDateChange(newDate) && endDateChange(moment(newDate).add(1, 'day')) : endDateChange(newDate);
    // }

    state = {
        startTime: {
            hour: moment().startOf('day').format('h'),
            minute: moment().startOf('day').minute(),
            ampm: moment().startOf('day').format('a'),
        },
        endTime: {
            hour: moment().endOf('day').format('h'),
            minute: moment().endOf('day').minute(),
            ampm: moment().endOf('day').format('a'),
        },
        range: null,
    }

    __change = (newRange: {from: Date, to: Date}) => {
        const { startDateChange, endDateChange } = this.props;
        const { startTime, endTime } = this.state;
        const startTimeParsed = moment(`${startTime.hour}:${startTime.minute} ${startTime.ampm}`, [`hh:mm A`]);
        const endTimeParsed = moment(`${endTime.hour}:${endTime.minute} ${endTime.ampm}`, [`hh:mm A`]);

        if (newRange.to === undefined || newRange.to === null) {
            newRange.to = newRange.from;
        }
        if (newRange.from === undefined || newRange.from === null) {
            newRange.from = newRange.to
        }
        startDateChange(moment(newRange.from).set({hours: startTimeParsed.hours(), minutes: startTimeParsed.minutes()}));
        endDateChange(moment(newRange.to).set({hours: endTimeParsed.hours(), minutes: endTimeParsed.minutes()}));
        this.setState({range: newRange});

    }

    __handleStartTimeChange = ({hours, minutes, ampm}) => {
        this.setState({
            startTime: {hour: hours, minute: minutes, ampm}},
            () => this.state.range && this.__change(this.state.range)
        );
    }
    __handleEndTimeChange = ({hours, minutes, ampm}) => {
        this.setState(
            {endTime: {hour: hours, minute: minutes, ampm}},
            () => this.state.range && this.__change(this.state.range)
        );
    }

    debugRender = () => {
        const { initialStart: startDate, initialEnd: endDate, /*calendarTop, calendarHideable,*/ style={}, timeOptionsClassName } = this.props;
        const { startTime, endTime } = this.state;

        return (
            <div style={{display: 'flex', justifyContent: 'space-around', alignItems: 'flex-start', flex: 1, flexWrap: 'wrap', ...style}}>
                <RangeDayPicker defaultRange={{ startDate: startDate?.toDate(), endDate: endDate?.toDate()}} onRangeChange={this.__change} />
                <div className={classNames(timeOptionsClassName, "time-options-container")}>
                    <div className='time-option'>
                        <strong className="time-options-header">Start Date:</strong>
                        <TimeOptions
                            hours={+startTime.hour}
                            minutes={startTime.minute}
                            ampm={startTime.ampm}
                            onTimeChange={this.__handleStartTimeChange}
                            // hourChange={this.__changeHour(true)}
                            // minuteChange={this.__changeMinute(true)}
                            // ampmChange={this.__changeAmPm(true)}
                        />
                    </div>
                    <div className='time-option'>
                        <strong className="time-options-header">End Date:</strong>
                        <TimeOptions
                            hours={+endTime.hour}
                            minutes={endTime.minute}
                            ampm={endTime.ampm}
                            onTimeChange={this.__handleEndTimeChange}
                            // hourChange={this.__changeHour(false)}
                            // minuteChange={this.__changeMinute(false)}
                            // ampmChange={this.__changeAmPm(false)}
                        />
                    </div>
                </div>
            </div>
        )
    }
}

type TimeOptionsProps = {
    hours: number,
    minutes: number,
    ampm: string,
    onTimeChange: (time: {hours: number, minutes: number, ampm: string}) => void
}

const TimeOptions: FC<TimeOptionsProps> = ({hours, minutes, ampm, onTimeChange}) => {
    const handleTimeChange = ({target: {value, name}}) => {
        onTimeChange({
            hours, minutes, ampm,
            [name]: value
        });
    }

    return (
        <div className="time-options">
            <select className="time-options__mas-input" name="hours" onChange={handleTimeChange} value={hours}>
                { simpleHours }
            </select>

            <select className="time-options__mas-input" name="minutes" onChange={handleTimeChange} value={minutes}>
                { minuteOptions }
            </select>
            <select className="time-options__mas-input" name="ampm" onChange={handleTimeChange} value={ampm}>
                <option value="am">AM</option>
                <option value="pm">PM</option>
            </select>
        </div>
    );
}

const minuteOptions = range(0, 59).map(m => <option key={m} value={m}>{m < 10 ? `0${m}` : m}</option>);
const simpleHours = range(1,12).map(h => <option key={h} value={h}>{h}</option>);

/*const hourOptions = range(0, 23).map(hour => {
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const humanHour = (hour > 12 ? hour - 12 : hour) || 12;

    const dispHour = `${humanHour}`.length > 1 ? `${humanHour}` : ` ${humanHour}`

    return <option key={hour} value={hour}>{dispHour}:00 {ampm}</option>
})*/

/*class DateTime extends BaseComponent<{ title, date, format, change, calendarTop, alwaysShow }, { showCal: boolean, tempstore?: any, calendarTop: boolean, alwaysShow: boolean, date: moment.Moment }> {
    state = {
        showCal: false || this.props.alwaysShow,
    } as any;

    private toggleCal = () => this.setState(s => ({ showCal: !s.showCal || this.props.alwaysShow }));

    private handleTimeChange = (type, isTwentyfour: boolean) => ({ target: { value }}) => {
        const { date, change } = this.props;

        // const initial = date.clone();

        if (!date) return this.setState(s => update(s, {
            tempstore: { [type]: { $set: value }}
        }))

        this.setState(s => ({
            tempstore: null
        }))

        if (type === 'minute') return change(date.clone().minute(value));

        if (type === 'hour') return change(date.clone().hour(
            // if the user is passing a 12 hour we need to add 12 if we are workign in the pm
            parseInt(value) + (!isTwentyfour && date.format('a') === 'pm' ? 12 : 0)

        ))

        if (type === 'ampm') {
            if (value === 'am' && date.format('a') === 'pm') {
                value = date.hour() - 12;
            } else if (value === 'pm' && date.format('a') === 'am') {
                value = date.hour() + 12;
            } else {
                value = date.hour();
            }

            return change(date.clone().hour(value));
        }
    }

    private changeDate = hour => date => {
        const asMoment = moment(date);

        if (this.state.tempHourStore) {
            asMoment.hour(this.state.tempHourStore);
            this.setState(s => ({
                tempHourStore: null
            } as any))
        } else if (hour) {
            asMoment.hour(hour);
        } else {
            asMoment.hour(0);
        }

        this.props.change(asMoment);

        this.toggleCal()
    }

    debugRender = () => {
        // const { title, date, format, calendarTop, alwaysShow } = this.props;
        const { date } = this.props;
        // const { showCal } = this.state;

        return (
            <DayPicker
                className="report-selectable"
                month={date ? date.toDate() : new Date()}
                onDayClick={this.changeDate(date ? date.hour() : 0)}
                selectedDays={[date.toDate()]}
            />
        )
    }



}*/

/*const rightAddonStyle: css = {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    border: `1px solid ${C.primaryColor}`,
    borderLeft: 'none',
    marginLeft: -1,
    padding: '0 5px',
    cursor: 'pointer',
}*/

